import { z } from 'zod';
import { Birthday } from './birthdays.js';
import { Role } from './roles.js';
import { UserStatus } from './status.js';
import { TimeZoneName, WorkingHours } from './zones.js';
/**
 * People's profiles (docs/adr/0032-profiles-and-local-time.md): the Slack-like panel that opens from
 * any name or avatar. Title, phone and about are per workspace membership; the time zone and working
 * hours are person settings (`general.timezone`, `general.timezoneAuto`, `general.workingHours`).
 */
/** Digits with the usual separators: "+61 400 123 456", "(02) 9000 0000", "+91-98765-43210". */
export const PhoneNumber = z
    .string()
    .trim()
    .max(32)
    .regex(/^\+?[0-9][0-9 ()\-.]{4,30}$/, 'Use digits, spaces and an optional leading +')
    .refine((v) => (v.match(/\d/g) ?? []).length >= 5, { message: 'That number looks too short' });
export const PROFILE_LIMITS = { title: 80, about: 280 };
export const Profile = z.object({
    userId: z.uuid(),
    name: z.string(),
    initials: z.string().max(3),
    color: z.number().int().min(0).max(7),
    role: Role,
    title: z.string().nullable(),
    about: z.string().nullable(),
    /**
     * Null when not set, or when the viewer may not see it: the person switched it off ("Show my email to
     * teammates", "Show my phone to teammates"), or the viewer is a guest (docs/adr/0047). Never sent then.
     */
    email: z.string().nullable(),
    phone: z.string().nullable(),
    /** IANA name. Null when the workspace hides local times (privacy.showLocalTime off). */
    timeZone: z.string().nullable(),
    /** Null when not set or when local times are hidden. */
    workingHours: WorkingHours.nullable(),
    status: UserStatus.nullable(),
    isSelf: z.boolean(),
    /** The viewer can open a DM with them (guests need chat.guestsCanDm). */
    canMessage: z.boolean(),
    /** Only on your own profile: the time zone follows your device. */
    timeZoneAuto: z.boolean().nullable(),
    /** Only on your own profile (docs/adr/0047): day and month, never a year. Null when not given. */
    birthday: Birthday.nullable(),
    /** Only on your own profile: "Celebrate my birthday". */
    celebrateBirthday: z.boolean().nullable(),
    /** Only on your own profile: "Show my email to teammates" and "Show my phone to teammates". */
    showEmail: z.boolean().nullable(),
    showPhone: z.boolean().nullable(),
    /** Only on your own profile: the workspace has birthday celebrations on (privacy.birthdays). */
    birthdaysOn: z.boolean().nullable(),
});
/** Empty text clears a field. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length ? v : null))
    .nullable()
    .optional();
export const UpdateMyProfileInput = z
    .object({
    title: optionalText(PROFILE_LIMITS.title),
    about: optionalText(PROFILE_LIMITS.about),
    phone: z.union([z.literal(''), PhoneNumber]).transform((v) => (v ? v : null)).nullable().optional(),
    /** Choosing a zone turns "Automatically set from this device" off, unless timeZoneAuto: true comes with it. */
    timeZone: TimeZoneName.optional(),
    timeZoneAuto: z.boolean().optional(),
    /** Null clears them ("I don't keep set hours"). */
    workingHours: WorkingHours.nullable().optional(),
    /** Day and month only; a body with a year is refused. Null clears it (docs/adr/0047). */
    birthday: Birthday.nullable().optional(),
    celebrateBirthday: z.boolean().optional(),
    showEmail: z.boolean().optional(),
    showPhone: z.boolean().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Nothing to change' });
/** Sent by the web and phone apps on start and whenever the device's zone changes. */
export const DeviceTimeZoneInput = z.object({ timeZone: TimeZoneName });
export const DeviceTimeZoneResponse = z.object({
    /** The person's effective zone after the call. */
    timeZone: z.string(),
    auto: z.boolean(),
    /** True when the call moved their zone. */
    changed: z.boolean(),
});
// --- Profile photos (docs/adr/0075) -----------------------------------------------------------------
/**
 * A profile photo is made from an ordinary upload (createUpload → PUT → completeUpload, so the same size limit
 * and blocked-type rules apply), then handed over here. The API reads the bytes, accepts only PNG, JPEG or WebP by
 * their signature, decodes them with a pixel limit, crops to a centred square, re-encodes a 256 px WebP without any
 * metadata (EXIF, GPS, colour profiles) and deletes the upload.
 */
export const AVATAR_LIMITS = {
    /** The largest original accepted, whatever the workspace's own upload limit. */
    maxBytes: 10 * 1024 * 1024,
    /** Decoded size limit (a decompression bomb is refused before it is decoded). */
    maxPixels: 40_000_000,
    /** The stored square's side, enough for a 96 px circle on a 2.5x screen. */
    size: 256,
    types: ['image/png', 'image/jpeg', 'image/webp'],
};
export const SetMyAvatarInput = z.object({ fileId: z.uuid() });
/** Changes with every new photo: clients put it in the image URL so caches never show an old one. */
export const AvatarVersion = z.string().regex(/^[0-9a-f]{16}$/);
export const MyAvatar = z.object({ userId: z.uuid(), version: AvatarVersion.nullable() });
/** Who has a photo among the people the caller may see (guests: people they share a channel with). */
export const PeopleAvatars = z.object({ items: z.array(z.object({ userId: z.uuid(), version: AvatarVersion })) });
export const AvatarQuery = z.object({ v: AvatarVersion.optional() });
/** The image path for a person's photo; `version` from PeopleAvatars or MyAvatar. */
export const avatarPath = (userId, version) => `/v1/people/${encodeURIComponent(userId)}/avatar?v=${version}`;
//# sourceMappingURL=profiles.js.map